import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { SecurityCategory_CategoryCode, securityCategory_CategoryCodeToJSON } from '../../proto/generated/security'
import { useDispatch } from 'react-redux'
import { useCallback, useEffect, useRef, useState } from 'react'
import commonActions from '../../actions/common.actions'
import { Avatar, Grid, ListItem, ListItemButton, ListItemSecondaryAction, Stack, Typography, useTheme } from '@mui/material'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { getColorForSymbol } from '../../util/general'
import TextHighlighter from '../common/TextHighlighter'
import { Add, Delete, Remove } from '@mui/icons-material'
import { ExtraSmallButtonHoverable } from '../custom'
import AnalyticsManager from '../../analytics/AnalyticsManager'

interface SecurityRowProps {
    symbol: string
    series?: string
    displayName: string
    exchange?: Exchange
    tailIcon?: boolean
    deleteIcon?: boolean
    categoryCode: SecurityCategory_CategoryCode
    highlightSelected?: boolean
    logo?: any
    logoUrl?: string
    selected?: boolean
    expression?: string
    onSecurityDeleted?: any
    isExpression?: boolean
    colorSymbol?: string
    searchTerm: string
    focused: boolean
    onSecuritySelect?: any
    expressionMode: boolean
}

const safeString = (v: unknown): string => (typeof v === 'string' ? v : '')

const SecurityRow = ({
    symbol,
    series,
    displayName,
    exchange,
    categoryCode,
    logo,
    logoUrl,
    selected: itemSelected,
    searchTerm,
    colorSymbol,
    onSecuritySelect,
    onSecurityDeleted,
    isExpression = false,
    highlightSelected = false,
    focused,
    tailIcon = false,
    deleteIcon = false,
    expressionMode
}: SecurityRowProps) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const [hovered, setHovered] = useState(false)
    const targetRef = useRef<HTMLLIElement>(null)
    const [selected, setSelected] = useState<boolean>(false)

    const handleDeleteClick = useCallback(
        (e: any) => {
            if (!onSecurityDeleted) {
                commonActions.setApiError(dispatch, { error: true, message: 'Something went wrong', errorType: 'error' })
                return
            }
            onSecurityDeleted()
            e.stopPropagation()
        },
        [onSecurityDeleted, dispatch]
    )

    useEffect(() => {
        if (focused && targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
        }
        if (focused) {
            onSecuritySelect(selected, focused)
        }
    }, [focused])

    useEffect(() => {
        setSelected(itemSelected ?? false)
    }, [itemSelected])

    return (
        <ListItem
            disablePadding
            className={'security-item'}
            sx={{ borderBottom: 0.5, borderColor: 'divider' }}
            onClick={() => {
                AnalyticsManager.logEvent('Symbol Clicked', { source: 'symbol-search', symbol: symbol })
                !expressionMode && setSelected(!selected)
                onSecuritySelect(!selected, false)
            }}
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            ref={targetRef}
        >
            <ListItemButton disableGutters={true} selected={focused || (highlightSelected && selected)}>
                <Grid container justifyContent={'center'} spacing={1}>
                    <Grid item xs={4}>
                        <Stack direction="row" spacing={1} sx={{ ml: 3 }}>
                            {logo ? (
                                <Avatar
                                    alt={safeString(displayName)}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        background: (theme) => theme.palette.background.default
                                    }}
                                >
                                    {logo}
                                </Avatar>
                            ) : logoUrl ? (
                                <Avatar alt={safeString(displayName)} src={`${LOGOS_BASE_URL}${logoUrl}.svg`} sx={{ width: 24, height: 24 }} />
                            ) : (
                                <Avatar
                                    alt={safeString(displayName)}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: 12,
                                        color: 'white',
                                        bgcolor: getColorForSymbol(colorSymbol ?? symbol, theme.palette.background.default)
                                    }}
                                >
                                    {(symbol || '')
                                        .split(' ')
                                        .map((word) => word[0]?.toUpperCase())
                                        .slice(0, 2)
                                        .join('')}
                                </Avatar>
                            )}
                            <Typography noWrap variant={'body1'} sx={{ lineHeight: '24px' }}>
                                <TextHighlighter textToHighlight={safeString(symbol)} searchTerm={searchTerm} />
                                {series && series !== 'EQ' && series !== 'XX' ? '-' + series : ''}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ lineHeight: '24px' }} variant={'body2'} noWrap>
                            <TextHighlighter textToHighlight={safeString(displayName)} searchTerm={searchTerm} />
                        </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: 'end' }}>
                        <Typography
                            component="span"
                            color={'textSecondary'}
                            noWrap
                            variant={'body2'}
                            sx={{
                                textAlign: 'end',
                                fontSize: 12,
                                lineHeight: '24px',
                                paddingRight: 1
                            }}
                        >
                            {(securityCategory_CategoryCodeToJSON(categoryCode) ?? '').toLowerCase()}
                        </Typography>
                        <Typography
                            component="span"
                            noWrap
                            variant={'body2'}
                            sx={{
                                textAlign: 'end',
                                lineHeight: '24px',
                                paddingRight: 4
                            }}
                        >
                            {exchangeToJSON(exchange ?? Exchange.NSE)}
                        </Typography>
                    </Grid>
                </Grid>
                {isExpression && deleteIcon && hovered && (
                    <ListItemSecondaryAction sx={{ paddingRight: 1 }}>
                        <ExtraSmallButtonHoverable variant={'outlined'} color={'error'} size={'small'} onClick={handleDeleteClick}>
                            <Delete fontSize={'small'} />
                        </ExtraSmallButtonHoverable>
                    </ListItemSecondaryAction>
                )}
                {tailIcon && hovered && (
                    <ListItemSecondaryAction sx={{ paddingRight: 1 }}>
                        <ExtraSmallButtonHoverable
                            variant={'outlined'}
                            color={selected ? 'error' : 'primary'}
                            size={'small'}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                !expressionMode && setSelected(!selected)
                                onSecuritySelect(!selected, false)
                            }}
                        >
                            {selected ? <Remove fontSize={'small'} /> : <Add fontSize={'small'} />}
                        </ExtraSmallButtonHoverable>
                    </ListItemSecondaryAction>
                )}
            </ListItemButton>
        </ListItem>
    )
}

export default SecurityRow
